import { coverageById, } from '@cod/config-schema';
/**
 * The cascading province → city → postal dropdowns, as a pure derivation.
 *
 * Ports `wireCoverage` (`form-renderer/src/form.ts`), and this is the clearest case in the whole
 * migration of the new model being **simpler**, not merely different. The original held a chain of
 * live `<select>` elements, listened for `change`, and imperatively rebuilt each downstream select's
 * options and disabled state — order-dependent mutation that had to reset children whenever a parent
 * changed.
 *
 * Here the caller owns the values and the builder re-runs on every change, so a level's options are
 * just "walk the dataset using the values above me". No listeners, no resets, no ordering. Selecting
 * a new province automatically empties the city list because the walk lands somewhere else.
 *
 * One behaviour has to be preserved deliberately: **a stale downstream value.** Changing province
 * leaves the old city in `state.values`, and the DOM version dropped it as a side effect of
 * rebuilding options. `coverageValue()` re-checks membership so a value no longer valid at its level
 * reads as empty — otherwise the buyer could submit a city that does not exist in their province.
 */
/**
 * The coverage chain for this config, or an empty chain when none is configured.
 *
 * `config.coverage` stores only `{ id, country }` — the field chain lives on the catalogue entry
 * (`COVERAGE_CATALOG`), because the number of cascading levels is a property of the dataset, not of
 * the merchant's choice. A 3-level dataset needs 3 selects whatever the config says.
 */
export function coverageChain(config) {
    return coverageById(config.coverage?.id)?.fields ?? [];
}
/** Where `key` sits in the chain, or -1 when it is not a coverage field. */
export function chainIndex(config, key) {
    return coverageChain(config).indexOf(key);
}
/**
 * The dataset node reached by the first `upTo` selections, or null when any of them is unfilled or
 * no longer valid. `true` is the dataset's leaf marker.
 */
function levelAt(chain, data, values, upTo) {
    let level = data;
    for (let i = 0; i < upTo; i++) {
        const key = chain[i];
        const value = key ? values[key] : undefined;
        if (!value || level === true)
            return null;
        const next = level[value];
        if (next === undefined)
            return null;
        level = next;
    }
    return level;
}
/**
 * Options for the coverage field at `index`, sorted.
 *
 * Empty means "nothing to choose yet" — the parent is unselected — and the caller disables the
 * select, exactly as `populate()` did (`sel.disabled = keys.length === 0`).
 */
export function coverageOptions(config, data, state, index) {
    if (!data)
        return [];
    const chain = coverageChain(config);
    if (index < 0 || index >= chain.length)
        return [];
    const parent = levelAt(chain, data, state.values, index);
    return parent && parent !== true ? Object.keys(parent).sort() : [];
}
/**
 * The effective value of a coverage field: its stored value, or `''` when that value is no longer
 * valid at this level.
 *
 * Guards the stale-downstream case described above. Without it, picking Bogotá then switching
 * province to Antioquia would submit "Antioquia / Bogotá".
 */
export function coverageValue(config, data, state, index) {
    const chain = coverageChain(config);
    const key = chain[index];
    if (!key)
        return '';
    const value = state.values[key] ?? '';
    if (!value)
        return '';
    return coverageOptions(config, data, state, index).includes(value) ? value : '';
}
/**
 * Keys to clear when the coverage field at `index` changes.
 *
 * Everything downstream of it: a new province invalidates the chosen city and postal code. The
 * caller applies this to `FormState` alongside the new value.
 */
export function dependentKeys(config, index) {
    return coverageChain(config).slice(index + 1);
}
//# sourceMappingURL=coverage.js.map